import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const fonts = {
  mono: "'JetBrains Mono', 'Fira Code', monospace",
  sans: "'Inter', -apple-system, sans-serif",
};

function valueColor(v) {
  if (v >= 0.7) return '#10B981';
  if (v >= 0.5) return '#F5C542';
  if (v >= 0.3) return '#F97316';
  return '#EF4444';
}

function flowOpacity(v) {
  return Math.max(0.15, Math.min(0.8, v || 0.5));
}

export default function EmergenceCascade({
  domains, lambda, viability, entropy, coherence,
  freeEnergy, omega, trajectory, ers, emergenceIndex,
  onDomainClick, onMetricClick,
}) {
  const { t } = useTranslation();
  const W = 920;
  const H = 620;

  const domainList = useMemo(() => {
    const order = ['physical', 'emotional', 'mental', 'spiritual', 'relational', 'purpose', 'alimento'];
    const labels = {
      physical: { name: t('domains.physical.label'), icon: '💚', color: '#10B981' },
      emotional: { name: t('domains.emotional.label'), icon: '💛', color: '#F59E0B' },
      mental: { name: t('domains.mental.label'), icon: '💙', color: '#3B82F6' },
      spiritual: { name: t('domains.spiritual.label'), icon: '💜', color: '#8B5CF6' },
      relational: { name: t('domains.relational.label'), icon: '🩷', color: '#EC4899' },
      purpose: { name: t('domains.purpose.label'), icon: '🧡', color: '#F97316' },
      alimento: { name: t('domains.alimento.label'), icon: '💚', color: '#84CC16' },
    };
    return order.map(k => ({
      key: k,
      value: domains?.[k] || 0.5,
      ...labels[k],
    }));
  }, [domains, t]);

  // Layer positions (Y)
  const Y = { domains: 55, lambda: 175, derived: 290, composite: 405, ers: 490, emergence: 570 };
  const centerX = W / 2;

  // Layer 2: derived metrics positions
  const derivedMetrics = [
    { key: 'V', id: 'viability', label: t('metrics.viability'), value: viability, x: centerX - 160, color: '#10B981' },
    { key: 'S', id: 'entropy', label: t('metrics.entropy'), value: entropy, x: centerX, color: '#EF4444' },
    { key: 'C', id: 'coherence', label: t('metrics.coherence'), value: coherence, x: centerX + 160, color: '#06B6D4' },
  ];

  // Layer 3: composite metrics
  const compositeMetrics = [
    { key: 'F', id: 'freeEnergy', label: t('misc.freeEnergy'), value: freeEnergy, x: centerX - 120, color: '#F97316' },
    { key: 'Ω', id: 'omega', label: t('misc.consciousness'), value: omega, x: centerX, color: '#C084FC' },
    { key: 'Q', id: 'trajectory', label: t('misc.trajectory'), value: trajectory, x: centerX + 120, color: '#818CF8' },
  ];

  return (
    <div style={{
      background: 'linear-gradient(180deg, rgba(13,11,26,0.95) 0%, rgba(20,15,40,0.95) 100%)',
      border: '1px solid rgba(192,132,252,0.15)', borderRadius: 16,
      padding: '16px 8px', overflow: 'hidden',
    }}>
      <div style={{
        textAlign: 'center', marginBottom: 12,
        fontFamily: fonts.mono, fontSize: 10, letterSpacing: '0.2em', color: '#C084FC',
      }}>
        CASCADA CAUSAL — DE LOS DOMINIOS A LA EMERGENCIA
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
        <defs>
          {/* Glow filter */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id="glowStrong" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          {/* Gradients for flows */}
          <linearGradient id="flowDown" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C084FC" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#06B6D4" stopOpacity="0.3" />
          </linearGradient>
          <linearGradient id="flowGold" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F5C542" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0.3" />
          </linearGradient>
          <linearGradient id="flowEmerge" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C084FC" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#F5C542" stopOpacity="0.8" />
          </linearGradient>
        </defs>

        {/* ═══ LAYER LABELS ═══ */}
        <text x="12" y={Y.domains} fill="#8B85A0" fontSize="8" fontFamily={fonts.mono} opacity="0.6">L7: INPUT</text>
        <text x="12" y={Y.lambda} fill="#8B85A0" fontSize="8" fontFamily={fonts.mono} opacity="0.6">L1: LOGOS</text>
        <text x="12" y={Y.derived} fill="#8B85A0" fontSize="8" fontFamily={fonts.mono} opacity="0.6">L2-L5</text>
        <text x="12" y={Y.composite} fill="#8B85A0" fontSize="8" fontFamily={fonts.mono} opacity="0.6">L6: Ω</text>
        <text x="12" y={Y.ers} fill="#8B85A0" fontSize="8" fontFamily={fonts.mono} opacity="0.6">L8: ERS</text>
        <text x="12" y={Y.emergence} fill="#8B85A0" fontSize="8" fontFamily={fonts.mono} opacity="0.6">L9: E(x)</text>

        {/* ═══ FLOW LINES: Domains → Lambda ═══ */}
        {domainList.map((d, i) => {
          const dx = 100 + i * 110;
          const thickness = Math.max(1, d.value * 3);
          return (
            <path
              key={`flow-d-${d.key}`}
              d={`M ${dx} ${Y.domains + 22} Q ${(dx + centerX) / 2} ${Y.lambda - 30} ${centerX} ${Y.lambda - 18}`}
              stroke={d.color} strokeWidth={thickness} fill="none"
              opacity={flowOpacity(d.value)} strokeLinecap="round"
            />
          );
        })}

        {/* ═══ FLOW LINES: Lambda → Derived ═══ */}
        {derivedMetrics.map((m) => (
          <path
            key={`flow-l-${m.key}`}
            d={`M ${centerX} ${Y.lambda + 22} Q ${(centerX + m.x) / 2} ${Y.derived - 30} ${m.x} ${Y.derived - 18}`}
            stroke={m.color} strokeWidth={Math.max(1.5, (m.value || 0.5) * 3)} fill="none"
            opacity={flowOpacity(m.value)} strokeLinecap="round"
          />
        ))}

        {/* ═══ FLOW LINES: Derived → Composite ═══ */}
        {derivedMetrics.map((dm) =>
          compositeMetrics.map((cm) => (
            <path
              key={`flow-dc-${dm.key}-${cm.key}`}
              d={`M ${dm.x} ${Y.derived + 22} Q ${(dm.x + cm.x) / 2} ${Y.composite - 25} ${cm.x} ${Y.composite - 18}`}
              stroke="url(#flowDown)" strokeWidth={0.8} fill="none"
              opacity={0.15} strokeLinecap="round"
            />
          ))
        )}

        {/* ═══ FLOW LINES: Composite → ERS ═══ */}
        {compositeMetrics.map((m) => (
          <path
            key={`flow-c-${m.key}`}
            d={`M ${m.x} ${Y.composite + 22} Q ${(m.x + centerX) / 2} ${Y.ers - 20} ${centerX} ${Y.ers - 14}`}
            stroke="url(#flowGold)" strokeWidth={1.5} fill="none"
            opacity={0.3} strokeLinecap="round"
          />
        ))}

        {/* ═══ FLOW LINE: ERS → Emergence ═══ */}
        <path
          d={`M ${centerX} ${Y.ers + 20} L ${centerX} ${Y.emergence - 20}`}
          stroke="url(#flowEmerge)" strokeWidth={Math.max(2, (ers || 0.5) * 5)} fill="none"
          opacity={0.6} strokeLinecap="round"
        />

        {/* ═══ DOMAIN NODES (clickable) ═══ */}
        {domainList.map((d, i) => {
          const dx = 100 + i * 110;
          return (
            <g key={`node-d-${d.key}`}
              style={{ cursor: onDomainClick ? 'pointer' : 'default' }}
              onClick={() => onDomainClick?.(d.key)}
              onMouseEnter={(e) => { e.currentTarget.querySelector('rect').setAttribute('stroke-width', '2.5'); e.currentTarget.querySelector('rect').setAttribute('fill', `${d.color}30`); }}
              onMouseLeave={(e) => { e.currentTarget.querySelector('rect').setAttribute('stroke-width', '1'); e.currentTarget.querySelector('rect').setAttribute('fill', `${d.color}15`); }}
            >
              <rect x={dx - 42} y={Y.domains - 18} width={84} height={40} rx={8}
                fill={`${d.color}15`} stroke={d.color} strokeWidth={1} opacity={0.9}
                style={{ transition: 'all 0.2s ease' }} />
              <text x={dx} y={Y.domains - 2} textAnchor="middle" fill={d.color}
                fontSize="9" fontFamily={fonts.mono} fontWeight="600" style={{ pointerEvents: 'none' }}>
                {d.name}
              </text>
              <text x={dx} y={Y.domains + 14} textAnchor="middle" fill={valueColor(d.value)}
                fontSize="12" fontFamily={fonts.mono} fontWeight="700" style={{ pointerEvents: 'none' }}>
                {d.value.toFixed(2)}
              </text>
            </g>
          );
        })}

        {/* ═══ LAMBDA NODE — Central (clickable) ═══ */}
        <g filter="url(#glow)"
          style={{ cursor: onMetricClick ? 'pointer' : 'default' }}
          onClick={() => onMetricClick?.('lambda')}
          onMouseEnter={(e) => { e.currentTarget.querySelector('circle').setAttribute('stroke-width', '3.5'); }}
          onMouseLeave={(e) => { e.currentTarget.querySelector('circle').setAttribute('stroke-width', '2'); }}
        >
          <circle cx={centerX} cy={Y.lambda} r={30} fill="rgba(192,132,252,0.12)"
            stroke="#C084FC" strokeWidth={2} style={{ transition: 'all 0.2s ease' }} />
          <text x={centerX} y={Y.lambda - 8} textAnchor="middle" fill="#C084FC"
            fontSize="16" fontFamily={fonts.mono} fontWeight="800" style={{ pointerEvents: 'none' }}>Λ</text>
          <text x={centerX} y={Y.lambda + 10} textAnchor="middle" fill={valueColor(lambda)}
            fontSize="14" fontFamily={fonts.mono} fontWeight="700" style={{ pointerEvents: 'none' }}>
            {(lambda || 0).toFixed(3)}
          </text>
        </g>
        <text x={centerX} y={Y.lambda + 48} textAnchor="middle" fill="#8B85A0"
          fontSize="8" fontFamily={fonts.mono} opacity="0.7">
          Alineación con el Logos
        </text>

        {/* ═══ DERIVED METRICS NODES (clickable) ═══ */}
        {derivedMetrics.map((m) => (
          <g key={`node-${m.key}`}
            style={{ cursor: onMetricClick ? 'pointer' : 'default' }}
            onClick={() => onMetricClick?.(m.id)}
            onMouseEnter={(e) => { e.currentTarget.querySelector('rect').setAttribute('stroke-width', '2.5'); e.currentTarget.querySelector('rect').setAttribute('fill', `${m.color}25`); }}
            onMouseLeave={(e) => { e.currentTarget.querySelector('rect').setAttribute('stroke-width', '1.2'); e.currentTarget.querySelector('rect').setAttribute('fill', `${m.color}10`); }}
          >
            <rect x={m.x - 50} y={Y.derived - 18} width={100} height={40} rx={10}
              fill={`${m.color}10`} stroke={m.color} strokeWidth={1.2}
              style={{ transition: 'all 0.2s ease' }} />
            <text x={m.x - 30} y={Y.derived + 2} fill={m.color}
              fontSize="12" fontFamily={fonts.mono} fontWeight="700" style={{ pointerEvents: 'none' }}>{m.key}</text>
            <text x={m.x + 30} y={Y.derived + 2} textAnchor="end" fill={valueColor(m.value)}
              fontSize="13" fontFamily={fonts.mono} fontWeight="700" style={{ pointerEvents: 'none' }}>
              {(m.value || 0).toFixed(3)}
            </text>
            <text x={m.x} y={Y.derived + 32} textAnchor="middle" fill="#8B85A0"
              fontSize="7" fontFamily={fonts.mono} opacity="0.6" style={{ pointerEvents: 'none' }}>{m.label}</text>
          </g>
        ))}

        {/* ═══ COMPOSITE METRICS NODES (clickable) ═══ */}
        {compositeMetrics.map((m) => (
          <g key={`node-c-${m.key}`}
            style={{ cursor: onMetricClick ? 'pointer' : 'default' }}
            onClick={() => onMetricClick?.(m.id)}
            onMouseEnter={(e) => { e.currentTarget.querySelector('rect').setAttribute('stroke-width', '2.5'); e.currentTarget.querySelector('rect').setAttribute('fill', `${m.color}25`); }}
            onMouseLeave={(e) => { e.currentTarget.querySelector('rect').setAttribute('stroke-width', '1'); e.currentTarget.querySelector('rect').setAttribute('fill', `${m.color}10`); }}
          >
            <rect x={m.x - 45} y={Y.composite - 18} width={90} height={36} rx={8}
              fill={`${m.color}10`} stroke={m.color} strokeWidth={1}
              style={{ transition: 'all 0.2s ease' }} />
            <text x={m.x - 25} y={Y.composite + 2} fill={m.color}
              fontSize="11" fontFamily={fonts.mono} fontWeight="600" style={{ pointerEvents: 'none' }}>{m.key}</text>
            <text x={m.x + 28} y={Y.composite + 2} textAnchor="end" fill={valueColor(m.value)}
              fontSize="12" fontFamily={fonts.mono} fontWeight="700" style={{ pointerEvents: 'none' }}>
              {(m.value || 0).toFixed(3)}
            </text>
          </g>
        ))}

        {/* ═══ ERS NODE (clickable) ═══ */}
        <g filter="url(#glow)"
          style={{ cursor: onMetricClick ? 'pointer' : 'default' }}
          onClick={() => onMetricClick?.('ers')}
          onMouseEnter={(e) => { e.currentTarget.querySelector('rect').setAttribute('stroke-width', '3.5'); }}
          onMouseLeave={(e) => { e.currentTarget.querySelector('rect').setAttribute('stroke-width', '2'); }}
        >
          <rect x={centerX - 70} y={Y.ers - 16} width={140} height={36} rx={18}
            fill={`${valueColor(ers)}15`} stroke={valueColor(ers)} strokeWidth={2}
            style={{ transition: 'all 0.2s ease' }} />
          <text x={centerX - 40} y={Y.ers + 4} fill={valueColor(ers)}
            fontSize="10" fontFamily={fonts.mono} fontWeight="600" letterSpacing="1" style={{ pointerEvents: 'none' }}>ERS</text>
          <text x={centerX + 40} y={Y.ers + 5} textAnchor="end" fill={valueColor(ers)}
            fontSize="16" fontFamily={fonts.mono} fontWeight="800" style={{ pointerEvents: 'none' }}>
            {(ers || 0).toFixed(3)}
          </text>
        </g>

        {/* ═══ EMERGENCE NODE — The Crown ═══ */}
        <g filter="url(#glowStrong)">
          <circle cx={centerX} cy={Y.emergence} r={28}
            fill={emergenceIndex > 0.6 ? 'rgba(245,197,66,0.15)' : 'rgba(192,132,252,0.10)'}
            stroke={emergenceIndex > 0.6 ? '#F5C542' : '#C084FC'} strokeWidth={2.5} />
          <text x={centerX} y={Y.emergence - 6} textAnchor="middle"
            fill={emergenceIndex > 0.6 ? '#F5C542' : '#C084FC'}
            fontSize="10" fontFamily={fonts.mono} fontWeight="600" letterSpacing="1">E(x)</text>
          <text x={centerX} y={Y.emergence + 12} textAnchor="middle"
            fill={valueColor(emergenceIndex)}
            fontSize="14" fontFamily={fonts.mono} fontWeight="800">
            {(emergenceIndex || 0).toFixed(3)}
          </text>
        </g>
        <text x={centerX} y={Y.emergence + 46} textAnchor="middle" fill="#F5C542"
          fontSize="9" fontFamily={fonts.mono} fontWeight="600" letterSpacing="2" opacity="0.8">
          ✦ EMERGENCE ✦
        </text>

        {/* Direction arrows on the sides */}
        <text x={W - 30} y={H / 2} textAnchor="middle" fill="#8B85A0" fontSize="18" opacity="0.15"
          transform={`rotate(90, ${W - 30}, ${H / 2})`}>
          CAUSA → EFECTO
        </text>
      </svg>
    </div>
  );
}
